import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { User } from "firebase/auth";
import { DocumentSnapshot } from "firebase/firestore";
import dreamWorldProvider from "../DreamWorldProvider";
import { Album, LibraryItem } from "../types/library";
import { RootState } from "./store";
import eventBus from "../utils/eventBus";

import { Timestamp } from "firebase/firestore";
import { Socials } from "../types/user";

export interface UserData {
  bio?: string;
  avatarUrl?: string;
  backImageUrl?: string;
  uid: string;
  username: string;
  email: string;
  createdAt: number;
  piAddress?: string;
  lastGenerationDate?: Timestamp;
  lastGenerationMedia?: string;
  imagesGeneratedCount?: number;
  videoGeneratedCount?: number;
  imageEditedCount?: number;
  eventsCreatedCount?: number;
  effectsCreatedCount?: number;
  contentPublishedCount?: number;
  generationCount?: number;
  following?: string[];
  followers?: number;
  isPartner?: boolean;
  socials?: Socials;
  flux?: number;
  lastClaimedTimestamp?: Timestamp;
  subscription?: {
    planId: string;
    startDate: Timestamp;
    endDate: Timestamp;
  };
  isProMember?: boolean;
  proSubscriptionStartDate?: string;
  proSubscriptionEndDate?: string;
}

export interface UserGlobalData {
  user: User;
  userData: UserData;
}

interface DreamWorldState {
  albumContent: Record<string, LibraryItem[]>;
  userGlobalData: UserGlobalData | null;
  loading: boolean;
  error: string | null;
  availableBots: any[];
  libraryContent: LibraryItem[];
  libraryHasMoreContent: boolean;

  contentLoading: "idle" | "pending" | "succeeded" | "failed";
  lastVisible: DocumentSnapshot<any> | null;
  albums: Album[];
  albumsLoading: "idle" | "pending" | "succeeded" | "failed";
  uploads: LibraryItem[];
  libraryLastVisible: DocumentSnapshot<any> | null;
  selectedLibraryItem: LibraryItem | null;
}

const initialState: DreamWorldState = {
  libraryLastVisible: null,
  albumContent: {},
  userGlobalData: null,
  loading: false,
  error: null,
  availableBots: [],
  libraryContent: [],
  libraryHasMoreContent: true,
  contentLoading: "idle",
  lastVisible: null,
  albums: [],
  albumsLoading: "idle",
  uploads: [],
  selectedLibraryItem: null,
};

export const toggleLikeContent = createAsyncThunk(
  "dreamworld/toggleLikeContent",
  async (
    { contentId, userId }: { contentId: string; userId: string },
    { getState }
  ) => {
    await dreamWorldProvider.toggleLikeContent(contentId, userId);
    const state = getState() as RootState;
    const item = state.dreamworld.libraryContent.find(
      (c) => c.id === contentId
    );
    return { contentId, isLiked: !item?.isLiked };
  }
);

export const toggleFollowCreator = createAsyncThunk(
  "dreamworld/toggleFollowCreator",
  async (
    { currentUserId, creatorId }: { currentUserId: string; creatorId: string },
    { getState }
  ) => {
    await dreamWorldProvider.toggleFollowCreator(currentUserId, creatorId);
    const state = getState() as RootState;
    const isFollowing =
      state.dreamworld.userGlobalData?.userData?.following?.includes(creatorId);
    return { creatorId, isFollowing: !isFollowing };
  }
);

export const DreamWorldSlice = createSlice({
  name: "dreamworld",
  initialState,
  reducers: {
    setSelectedLibraryItem: (
      state,
      action: PayloadAction<LibraryItem | null>
    ) => {
      state.selectedLibraryItem = action.payload;
    },
    reduxSetAlbumContent: (
      state,
      action: PayloadAction<{ albumId: string; content: LibraryItem[] }>
    ) => {
      state.albumContent[action.payload.albumId] = action.payload.content;
    },
    reduxAppendAlbumContent: (
      state,
      action: PayloadAction<{ albumId: string; content: LibraryItem[] }>
    ) => {
      const { albumId, content } = action.payload;
      if (!state.albumContent[albumId]) {
        state.albumContent[albumId] = [];
      }
      state.albumContent[albumId] = [
        ...state.albumContent[albumId],
        ...content,
      ];
    },
    reduxSetLibraryContent: (state, action: PayloadAction<LibraryItem[]>) => {
      state.libraryContent = [...state.libraryContent, ...action.payload];
    },
    reduxSetAlbums: (state, action: PayloadAction<Album[]>) => {
      state.albums = [...state.albums, ...action.payload];
    },
    setUserGlobalData: (
      state,
      action: PayloadAction<UserGlobalData | null>
    ) => {
      state.userGlobalData = action.payload;
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },
    updateUserData: (state, action: PayloadAction<Partial<UserData>>) => {
      if (state.userGlobalData && state.userGlobalData.userData) {
        state.userGlobalData.userData = {
          ...state.userGlobalData.userData,
          ...action.payload,
        };
      }
    },
    addLibraryItem: (state, action: PayloadAction<LibraryItem>) => {
      state.libraryContent.unshift(action.payload);
    },
    updateLibraryHasMoreContent(state, action: PayloadAction<boolean>) {
      state.libraryHasMoreContent = action.payload;
    },
    publishContent: (
      state,
      action: PayloadAction<{
        id: string;
        title: string;
        description: string;
        tags: string[];
        effectId?: string;
      }>
    ) => {
      const { id, title, description, tags, effectId } = action.payload;
      const itemIndex = state.libraryContent.findIndex(
        (item) => item.id === id
      );
      if (itemIndex !== -1) {
        state.libraryContent[itemIndex] = {
          ...state.libraryContent[itemIndex],
          isPublished: true,
          title,
          description,
          tags,
          effectId,
        };
      }
      if (typeof window !== "undefined") {
        try {
          const key = "imageGeneratorHistory2";
          const raw = window.localStorage.getItem(key);
          if (!raw) return; // nothing to update

          const history: any[] = JSON.parse(raw);
          const hIdx = history.findIndex((h) => h?.id === id);
          if (hIdx === -1) return; // not in history, no-op
          history[hIdx] = {
            ...history[hIdx],
            isPublished: true,
            title,
            description,
            tags,
          };

          window.localStorage.setItem(key, JSON.stringify(history));
          eventBus.emit("imageGeneratorHistory2:updated");
        } catch (err) {
          console.error("Failed to sync imageGeneratorHistory2:", err);
        }
      }
    },
    incrementImageEdits: (state) => {
      if (state.userGlobalData && state.userGlobalData.userData) {
        state.userGlobalData.userData.imageEditedCount =
          (state.userGlobalData.userData.imageEditedCount || 0) + 1;
      }
    },
    reduxSetUploads: (state, action: PayloadAction<LibraryItem[]>) => {
      state.uploads = action.payload;
    },
    reduxAddUpload: (state, action: PayloadAction<LibraryItem>) => {
      state.uploads.unshift(action.payload);
    },
    updateLibraryLastVisible: (
      state,
      action: PayloadAction<DocumentSnapshot<any> | null>
    ) => {
      state.libraryLastVisible = action.payload;
    },
    removeLibraryItem: (state, action: PayloadAction<string>) => {
      state.libraryContent = state.libraryContent.filter(
        (item) => item.id !== action.payload
      );
    },
    incrementAlbumItemCount: (state, action: PayloadAction<string>) => {
      const albumIndex = state.albums.findIndex(
        (album) => album.id === action.payload
      );
      if (albumIndex !== -1) {
        state.albums[albumIndex].itemCount += 1;
      }
    },
  },
  extraReducers: (builder) => {
    builder.addCase(toggleLikeContent.fulfilled, (state, action) => {
      const { contentId, isLiked } = action.payload;
      const itemIndex = state.libraryContent.findIndex(
        (item) => item.id === contentId
      );
      if (itemIndex !== -1) {
        state.libraryContent[itemIndex].isLiked = isLiked;
        state.libraryContent[itemIndex].likes += isLiked ? 1 : -1;
      }
    });
    builder.addCase(toggleFollowCreator.fulfilled, (state, action) => {
      const { creatorId, isFollowing } = action.payload;
      if (state.userGlobalData?.userData) {
        if (isFollowing) {
          state.userGlobalData.userData.following = [
            ...(state.userGlobalData.userData.following || []),
            creatorId,
          ];
        } else {
          state.userGlobalData.userData.following = (
            state.userGlobalData.userData.following || []
          ).filter((id) => id !== creatorId);
        }
      }
    });
  },
});

export const {
  publishContent,
  removeLibraryItem,
  incrementAlbumItemCount,
  reduxSetAlbumContent,
  reduxAppendAlbumContent,
  reduxSetLibraryContent,
  updateLibraryLastVisible,
  setUserGlobalData,
  setError,
  updateUserData,
  updateLibraryHasMoreContent,
  addLibraryItem,
  reduxSetAlbums,
  incrementImageEdits,
  reduxSetUploads,
  reduxAddUpload,
  setSelectedLibraryItem,
} = DreamWorldSlice.actions;

export const selectUserGlobalData = (state: RootState) =>
  state.dreamworld.userGlobalData;
export const selectLibraryContent = (state: RootState) =>
  state.dreamworld.libraryContent;
export const selectSelectedLibraryItem = (state: RootState) =>
  state.dreamworld.selectedLibraryItem;

export const selectAlbumContent = (state: RootState, albumId: string) =>
  state.dreamworld.albumContent[albumId] || [];
export const selectLibraryContentLoading = (state: RootState) =>
  state.dreamworld.contentLoading;
export const selectLibraryHasMoreContent = (state: RootState) =>
  state.dreamworld.libraryHasMoreContent;
export const selectLibraryLastVisible = (state: RootState) =>
  state.dreamworld.libraryLastVisible;
export const selectAlbums = (state: RootState) => state.dreamworld.albums;
export const selectAlbumsLoading = (state: RootState) =>
  state.dreamworld.albumsLoading;
export const selectUploads = (state: RootState) => state.dreamworld.uploads;

export default DreamWorldSlice.reducer;
